"use client";

import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setTenant, DEFAULT_TENANT, Tenant } from "@/store/slices/tenantSlice";
import LandingPage from "@/components/landing-page/LandingPage";

interface TenantHydratorProps {
    tenantData: Tenant;
    hasTenant: boolean;
    children: React.ReactNode;
}

/**
 * Client component that hydrates the Redux store with server-fetched tenant data.
 *
 * - If hasTenant is true: seeds Redux with tenantData and renders children.
 * - If hasTenant is false: seeds Redux with DEFAULT_TENANT and renders LandingPage.
 *
 * This runs once on mount to avoid re-dispatching on every render.
 */
export default function TenantHydrator({
    tenantData,
    hasTenant,
    children,
}: TenantHydratorProps) {
    const dispatch = useDispatch();
    const hasHydrated = useRef(false);

    useEffect(() => {
        if (hasHydrated.current) return;
        hasHydrated.current = true;

        if (hasTenant) {
            dispatch(setTenant(tenantData));
        } else {
            dispatch(setTenant(DEFAULT_TENANT));
        }
    }, [dispatch, tenantData, hasTenant]);

    // If no valid tenant, show the landing page regardless of which route was requested
    if (!hasTenant) {
        return <LandingPage />;
    }

    return <>{children}</>;
}
